import { Link, NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { NAV } from '@/constants/testIds';
import { COMPANY } from '@/data/site';
import { motion, AnimatePresence } from 'framer-motion';
import { AlphaTexLockup } from '@/components/brand/AlphaTexLogo';

const links = [
  { to: '/', label: 'Home', tid: NAV.home },
  { to: '/services', label: 'Services', tid: NAV.services },
  { to: '/projects', label: 'Projects', tid: NAV.projects },
  { to: '/about', label: 'About', tid: NAV.about },
  { to: '/contact', label: 'Contact', tid: NAV.contact },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/85 border-b border-black/10'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center group">
          <AlphaTexLockup />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              end={l.to === '/'}
              className={({ isActive }) =>
                `font-mono-label text-[11px] tracking-[0.22em] uppercase transition-colors ${
                  isActive ? 'text-[#1D4ED8]' : 'text-[#0A0A0A]/75 hover:text-[#0A0A0A]'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div className="flex flex-col items-end leading-tight">
            <a
              href={COMPANY.phoneHref}
              className="font-mono-label text-[11px] text-[#0A0A0A]/80 hover:text-[#1D4ED8] tracking-[0.2em] flex items-center gap-2"
            >
              <Phone className="w-3.5 h-3.5" /> {COMPANY.phone}
            </a>
            <a
              href={COMPANY.phoneAltHref}
              className="font-mono-label text-[10px] text-[#0A0A0A]/55 hover:text-[#1D4ED8] tracking-[0.2em] mt-0.5"
            >
              Direct · {COMPANY.phoneAlt}
            </a>
          </div>
          <Link
            to="/contact"
            data-testid={NAV.cta}
            className="group inline-flex items-center gap-2 bg-[#1D4ED8] text-white px-5 py-3 font-mono-label text-[11px] tracking-[0.22em] uppercase hover:bg-[#1E3A8A] transition-colors"
          >
            Free Estimate
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          className="lg:hidden text-[#0A0A0A] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.tid}-mobile`}
                  end={l.to === '/'}
                  onClick={() => setOpen(false)}
                  className="font-display uppercase text-2xl text-[#0A0A0A]"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#1D4ED8] text-white px-5 py-4 font-mono-label text-xs tracking-[0.22em] uppercase"
              >
                Free Estimate →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
